import { useEffect } from 'react';

import { useAdRouteParams } from './useAdRouteParams';
import { useAnalyticalDatasetVersionQuery } from '../api/useAnalyticalDatasetVersionQuery';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';

/**
 * Seeds the definition store from the dataset's version, independent of which
 * detail tab is mounted.
 *
 * Mounted at the page level so a refresh straight onto the Results tab still
 * hydrates the store — the Process button + its source-cohort gate read
 * `cohorts`/`variables` from the store, so without this a deep-link to Results
 * reports "no source cohort" until the Definition tab is visited.
 *
 * `setFromServer` no-ops on the same versionId (store wins after the first
 * seed), so running this alongside the Definition tab's own consumers is safe
 * and never clobbers local edits. The version query is shared/deduped by
 * TanStack, so extra callers add no network cost.
 */
export const useHydrateAnalyticalDatasetDefinition = () => {
  const { projectId, analyticalDatasetProfileId, analyticalDatasetVersionId } = useAdRouteParams();

  const versionQuery = useAnalyticalDatasetVersionQuery({
    projectId,
    analyticalDatasetProfileId,
    analyticalDatasetVersionId,
  });

  const setFromServer = useAnalyticalDatasetDefinitionStore((s) => s.setFromServer);
  const definition = versionQuery.data?.data.jsonValue ?? null;
  const hasData = versionQuery.isSuccess;

  useEffect(() => {
    if (!hasData) return;
    setFromServer({ versionId: analyticalDatasetVersionId, definition });
  }, [hasData, analyticalDatasetVersionId, definition, setFromServer]);

  return { versionQuery };
};
